import { macroRegionSchema } from '@playcastdotio/Messaging';
import type { ReferenceCaptureManager } from '../../services/playjector/managers/ReferenceCaptureManager';

interface ReferenceCaptureResponses {
  reply(peerId: string, action: string, result: Promise<unknown>): void;
}
const ACTIONS = new Set(['macroRecorderReferenceCaptureBegin', 'macroRecorderReferenceCaptureMark',
  'macroRecorderReferenceCaptureFocus', 'macroRecorderReferenceCaptureEnd']);

/** Routes reference commands after the coordinator has verified local editor ownership. */
export class LocalReferenceCaptureCommandHandler {
  public constructor(private readonly manager: ReferenceCaptureManager | undefined,
    private readonly responses: ReferenceCaptureResponses) {}

  public dispatch(peerId: string, action: string, payload: Record<string, unknown>): boolean {
    if (!ACTIONS.has(action)) return false;
    const requestId = String(payload.requestId ?? '');
    const result = this.manager ? this.invoke(action, requestId, payload)
      : Promise.resolve({ requestId, success: false, error: 'reference_capture_unavailable' });
    this.responses.reply(peerId, action, result);
    return true;
  }

  private invoke(action: string, requestId: string, payload: Record<string, unknown>): Promise<unknown> {
    const manager = this.manager!;
    const sessionId = String(payload.sessionId ?? '');
    const lookback = payload.lookbackMs;
    const lookbackMs = typeof lookback === 'number' && Number.isInteger(lookback) ? lookback : undefined;
    const region = macroRegionSchema.safeParse(payload.region);
    if (action === 'macroRecorderReferenceCaptureFocus') {
      if (!region.success) return Promise.resolve({ requestId, success: false, error: 'invalid_command' });
      manager.focus(sessionId, region.data);
      return Promise.resolve({ requestId, success: true });
    }
    const run = action === 'macroRecorderReferenceCaptureBegin'
      ? manager.begin(payload as never).then((armed) => ({ requestId, success: true, armed }))
      : action === 'macroRecorderReferenceCaptureMark'
        ? manager.mark(sessionId, String(payload.markId ?? ''), lookbackMs, region.success ? region.data : undefined)
          .then((marked) => ({ requestId, success: true, marked }))
        : manager.end(sessionId).then(() => ({ requestId, success: true }));
    return run.catch((error: unknown) => ({
      requestId, success: false, error: error instanceof Error ? error.message : 'reference_capture_request_failed',
    }));
  }
}
